/*
 * File: object-helper.ts
 * File Created: 2024-02-01 09:15:34
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-01-20 10:44:20
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import lodash from "lodash";
import { AnyHelper } from "./any-helper";

let Vue;
try {
    Vue = require("vue-property-decorator").Vue;
} catch (error) {
    /// fallback
}

export class ObjectHelper {
    /// remove the given keys
    static Omit<T, U extends keyof T>(value: T, ...keys: U[]): Omit<T, U> {
        let result = { ...value };
        keys.forEach((key) => delete result[key]);
        return result;
    }
    /// pick the given keys
    static Pick<T, U extends keyof T>(value: T, ...keys: U[]): Pick<T, U> {
        return keys.reduce((final, key) => {
            let val = value[key];
            if (val != undefined) final[key as any] = val;
            return final;
        }, {}) as any;
    }

    static equals(source: any, target: any): boolean {
        return lodash.isEqual(source, target);
    }

    static deepClone<T>(source: T): T {
        /// if it's a file, temporary return it.
        if (source instanceof File) return source;
        return lodash.cloneDeep(source);
    }

    /// loop for every key in the object `source`,
    /// if the `source[key]` typeof is "object", pass into transformer.
    /// 1) if the return value from transformer is `undefined`
    /// and the `source[key]` isPureObject, stop this level of looping,
    /// or else call `deepReplace(source[key], transformer)` and go deep on.
    /// 2) if the return value from transformer is not undefined,
    /// make the `source[key]` = the return value, and stop this level of looping.
    static deepReplace<T extends object>(
        source: T | undefined,
        transformer: { (input: any): NonNullable<any> | undefined },
    ): T | undefined {
        /// perform the transform
        const doTransform = (source, key) => {
            const originalValue = source[key];
            const transformedValue = transformer(originalValue);

            if (transformedValue !== undefined) {
                // If transformer returns a non-undefined value, replace and stop this level of looping.
                source[key] = transformedValue;
            } else if (AnyHelper.isPrimitive(originalValue)) {
                // If the original value is a primitive object and transformer returns undefined,
                // just return.
                return;
            } else {
                // array and pure object.
                source[key] = this.deepReplace(originalValue as T, transformer);
            }
        };

        if (source == undefined || typeof source !== "object") return source;

        if (Array.isArray(source)) {
            source.forEach((_, key) => {
                doTransform(source, key);
            });
        } else {
            Object.keys(source).forEach((key) => {
                doTransform(source, key);
            });
        }

        return source;
    }

    static deepMergeWithoutClone<T extends object>(
        source: T | undefined,
        ...targets: any
    ): NonNullable<T> {
        let merged = source ?? ({} as T);

        // Iterate over each target object
        targets.forEach((target) => {
            if (this.isPureObject(merged) && this.isPureObject(target)) {
                for (const key in target) {
                    if (
                        this.isPureObject(target[key]) &&
                        key in merged &&
                        this.isPureObject(merged[key])
                    ) {
                        merged[key] = this.deepMergeWithoutClone(
                            merged[key],
                            target[key],
                        );
                    } else if (target[key] !== undefined) {
                        merged[key] = target[key];
                    }
                }
            }
        });
        // If `target` is not an object or `source` is not an object, return `source`
        return merged;
    }

    /**
     * Deeply merges the properties of the provided target objects into the source object.
     * `source` is automatically cloned for the simple usage.
     * You have to manually clone the `targets` if needed before calling this method.
     *
     * @template T - The type of the source object.
     * @param source - The source object into which properties from the target objects will be merged. If undefined, an empty object will be used as the source.
     * @param targets - The target objects containing properties to be merged into the source. Each target object is merged in sequence.
     * @returns The source object with properties from the target objects merged in. If no source object is provided, a new object will be returned.
     *
     * @remarks
     * This method performs a deep merge, meaning it will recursively merge nested objects.
     */
    static deepMerge<T extends object>(
        source: T | undefined,
        ...targets: any
    ): NonNullable<T> {
        let clone = ObjectHelper.deepClone(source) ?? ({} as T);
        return this.deepMergeWithoutClone(clone, ...targets);
    }

    static wrap(selector: string | null | undefined, value: any): any {
        /// example 1: selector = "data", value = { name: "123" }
        /// the result would be: { data: { name: "123" } }
        /// example 2: selector = "data.special", value = { name: "123" }
        /// the result would be: { data: { special: { name: "123" } } }
        // Split the selector into parts
        if (selector == undefined) return value;
        const parts = selector.split(".");

        // Recursively create nested objects
        function createNestedObject(obj, keys) {
            let current = obj;
            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                if (!current[key]) {
                    current[key] = {};
                }
                current = current[key];
            }
            current[keys[keys.length - 1]] = value;
            return obj;
        }

        return createNestedObject({}, parts);
    }

    static unwrap(selector: string | null | undefined, value: any): any {
        /// example 1: selector = "data.special.name",
        ///            value = { data: { special: { name: "123" } } }
        /// the result would be: "123"

        if (selector == undefined || value == undefined) return undefined;

        // Split the selector by "." to navigate through the nested properties
        const keys = selector.split(".");

        // Use reduce to traverse the object
        return keys.reduce((currentValue, key) => {
            return currentValue ? currentValue[key] : undefined;
        }, value);
    }

    /// not array and not primitive
    static isPureObject(item: any): boolean {
        return (
            item &&
            typeof item === "object" &&
            !Array.isArray(item) &&
            !AnyHelper.isPrimitive(item)
        );
    }

    static isPlainObject(value: any): value is Record<string, any> {
        return (
            value != null && typeof value === "object" && Array.isArray(value) === false
        );
    }

    static GetValue(source: object, keys: string | string[]): any {
        if (typeof keys === "string") keys = keys.split(".");
        let val = source;
        for (let i = 0; i < keys.length; ++i) {
            let key = keys[i];
            val = val[key];
            if (val == null) return;
        }
        return val;
    }
    static SetValue(source: object, keys: string | string[], value: any) {
        if (typeof keys === "string") keys = keys.split(".");
        let val = source;
        let lastKey = keys[keys.length - 1];
        for (let i = 0; i < keys.length - 1; ++i) {
            let key = keys[i];
            val = val[key];
            if (val == null) return;
        }
        Vue.set(val, lastKey, value);
    }
    static DeleteValue(source: object, keys: string | string[]) {
        if (typeof keys === "string") keys = keys.split(".");
        let val = source;
        let lastKey = keys[keys.length - 1];
        for (let i = 0; i < keys.length - 1; ++i) {
            let key = keys[i];
            val = val[key];
            if (val == null) return;
        }
        delete val[lastKey];
    }

    /// remove object keys, ex: `_id`
    static removeObjectKeys(
        obj: any | Array<any>,
        tokens: (string | RegExp) | Array<string | RegExp>,
    ) {
        const tokenArray = Array.isArray(tokens) ? tokens : [tokens];

        if (Array.isArray(obj)) {
            obj.forEach((element) => {
                this.removeObjectKeys(element, tokenArray);
            });
        } else if (obj !== null && typeof obj === "object") {
            Object.keys(obj).forEach((key) => {
                if (
                    tokenArray.some((token) =>
                        typeof token === "string" ? key === token : token.test(key),
                    )
                ) {
                    delete obj[key];
                } else {
                    this.removeObjectKeys(obj[key], tokenArray);
                }
            });
        }
    }

    /// check object is empty
    static isEmpty(obj: any) {
        for (let prop in obj) {
            if (obj.hasOwnProperty(prop)) {
                return false;
            }
        }
        return true;
    }
}
